import { JobType } from '../../../redux/jobs/jobs.types';
import { MediumType, RemoteResult } from '../../common/RemoteResultCommand/index.types';
import RemoteResultHelper from '../../../helper/RemoteResultHelper';
import { NoMatchInfo } from '../aiReadingMachine';

export type MeterMatchResult = { type: 'match'; job: JobType } | { type: 'ambiguous'; jobs: JobType[] } | { type: 'none' };

export const normalizeMeterNumber = (value: string): string => value.replace(/[\s-]/g, '').toUpperCase();

// Core comparison shared by matchMeterNumberToJobs and resultIdentifiesMeter: same normalization,
// same trailing-chars tolerance. Keeping this in one place is deliberate — a second, subtly
// different comparison would risk the anti-cheat guard (below) drifting out of sync with the
// identify match it is supposed to mirror.
const meterNumbersMatch = (a: string, b: string, trailingChars?: number | null): boolean => {
    const normalizedA = normalizeMeterNumber(a);
    const normalizedB = normalizeMeterNumber(b);
    if (!normalizedA || !normalizedB) {
        return false;
    }
    if (trailingChars && trailingChars > 0) {
        return normalizedA.slice(-trailingChars) === normalizedB.slice(-trailingChars);
    }
    return normalizedA === normalizedB;
};

// Anti-cheat identity guard (Task 13): the shape stashed in AiReadingScreen's expectedMeterRef.
// `passThrough` marks the case where a job is matched but carries no known meter number to check
// identity against (see deriveExpectedMeter below) — there, guard call sites accept every result
// instead of dead-locking.
export type ExpectedMeter = { meterNumber: string; trailingCharsCount?: number | null; passThrough?: boolean };

// Anti-cheat identity guard (Task 13/follow-up): derives the expected meter identity from the
// matched job (if any), falling back to the machine's own meterNumber, and finally to null when
// no job is matched at all. Sourcing primarily from the job rather than the machine's meterNumber
// is deliberate — an earlier guard version gated on machine.meterNumber alone and blocked ALL
// automatic capture for flows that matched a job without setting it. Since the manual job
// selection (MANUAL_JOB_SELECTED) was removed, every remaining match path is meter-number-based,
// so a matched job without any known meter number should no longer occur; the fallback chain and
// the `passThrough: true` carve-out are kept as a defensive guard, because the alternative —
// resultIdentifiesMeter's "no expectedMeterNumber -> false" rule — would silently dead-lock
// capture forever if that state were ever reached again. Returns null whenever no job is matched
// at all (e.g. flow restart) so the guard still fails closed by default.
export const deriveExpectedMeter = (
    job: { meter?: { meterNumber?: string } } | undefined,
    fallbackMeterNumber: string | undefined,
    trailingCharsCount: number | null | undefined,
): ExpectedMeter | null => {
    const jobMeterNumber = job?.meter?.meterNumber || fallbackMeterNumber;
    if (!job) {
        return null;
    }
    if (jobMeterNumber) {
        return { meterNumber: jobMeterNumber, trailingCharsCount: trailingCharsCount };
    }
    // Matched job has no known meter number at all (should not occur since all match paths are
    // number-based; kept defensively) — nothing to check identity against. `passThrough` tells
    // the guard call sites to accept every result rather than reject everything.
    return { meterNumber: '', trailingCharsCount: trailingCharsCount, passThrough: true };
};

// True iff any job's expected registers belong to the given medium (job medium is derived from
// the tasks' OBIS codes — there is no explicit medium field on the job).
export const jobsIncludeMedium = (jobs: JobType[], medium: MediumType): boolean =>
    jobs.some((job) => (job.meter?.meterRateTasks ?? []).some((task) => RemoteResultHelper.resolveMeterMediumByObis(task.obis) === medium));

// Explains WHY an identify attempt failed so panel + announcement can be specific:
// - wrongMedium: the AI recognized a medium for which the user has no job at all — the classic
//   "standing in front of the gas meter while only an electricity reading is due" case.
// - numberMismatch: a meter number was read, but it matches no job.
// - notRecognized: nothing usable was read (also the identify-timeout default).
// ABSTRACT is the AI's "medium unknown" and must never count as a foreign medium.
export const buildNoMatchInfo = (result: Pick<RemoteResult, 'medium' | 'meterNumber'> | undefined, jobs: JobType[]): NoMatchInfo => {
    const detectedMedium = result?.medium;
    const detectedNumber = result?.meterNumber || undefined;
    if (detectedMedium && detectedMedium !== 'ABSTRACT' && !jobsIncludeMedium(jobs, detectedMedium)) {
        return { reason: 'wrongMedium', detectedMedium, detectedNumber };
    }
    if (detectedNumber) {
        return { reason: 'numberMismatch', detectedNumber, detectedMedium };
    }
    return { reason: 'notRecognized' };
};

export const matchMeterNumberToJobs = (detected: string, jobs: JobType[], trailingChars?: number): MeterMatchResult => {
    const needle = normalizeMeterNumber(detected ?? '');
    if (!needle) {
        return { type: 'none' };
    }

    const matches = jobs.filter((job) => meterNumbersMatch(job.meter?.meterNumber ?? '', needle, trailingChars));

    if (matches.length === 1) {
        return { type: 'match', job: matches[0] };
    }
    if (matches.length > 1) {
        return { type: 'ambiguous', jobs: matches };
    }
    return { type: 'none' };
};

// Anti-cheat identity guard (Task 13): a result may only feed value accumulation for the meter
// that was actually matched. True iff result.meterNumber OR any meterNumberCandidates[].id
// identifies expectedMeterNumber, using the identical normalization/trailing-chars tolerance the
// identify match used. A result with no readable number at all (neither field set) is REJECTED —
// a strict, deliberate product decision, not an oversight: silently accepting "unreadable" results
// is exactly the loophole a mismatched meter could exploit.
export const resultIdentifiesMeter = (
    result: Pick<RemoteResult, 'meterNumber' | 'meterNumberCandidates'>,
    expectedMeterNumber: string,
    trailingCharsCount?: number | null,
): boolean => {
    if (!expectedMeterNumber) {
        return false;
    }
    if (result.meterNumber && meterNumbersMatch(result.meterNumber, expectedMeterNumber, trailingCharsCount)) {
        return true;
    }
    return (result.meterNumberCandidates ?? []).some((candidate) => meterNumbersMatch(candidate.id, expectedMeterNumber, trailingCharsCount));
};
